import axios from 'axios'

import { isDevelopmentEnv } from '../config/config'
import { getVGSUrl } from '../helpers/utils'
import { CreditApplication } from '../models/cashSummary'
import { parseClickWrap } from '../models/docusign'
import { parsePlaidAccount, PlaidAccount } from '../models/plaid'

export interface CashSummary {
  lastLinkedPlaidAccount?: PlaidAccount
  lastVerifiedPlaidAccount?: PlaidAccount
  creditApplication?: CreditApplication
}

export enum PlaidReportStatus {
  PRODUCT_NOT_READY = 'PRODUCT_NOT_READY',
  PRODUCT_READY = 'PRODUCT_READY',
}

export interface PlaidReport {
  reportId: string
  status: PlaidReportStatus
  dateCreated: string
  daysRequested: number
  item: PlaidAccount['item']
}

export interface PlaidReports {
  lastReport: PlaidReport | undefined
  lastReadyReport: PlaidReport | undefined
}

export function fetchTeamCashSummary(teamId: string) {
  const url = `/api/v1/console/teams/${teamId}/cash-summary/`
  return axios.get(url).then((response) => {
    return {
      lastLinkedPlaidAccount: response.data.last_linked_plaid_account
        ? parsePlaidAccount(response.data.last_linked_plaid_account)
        : undefined,
      lastVerifiedPlaidAccount: response.data.last_verified_plaid_account
        ? parsePlaidAccount(response.data.last_verified_plaid_account)
        : undefined,
      ineligibleCreditStates: response.data.ineligible_credit_states || [],
      ineligibleSolePropCreditStates: response.data.ineligible_sole_prop_credit_states || [],
      creditApplication: response.data.credit_application
        ? {
            ...response.data.credit_application,
            dateApproved: response.data.credit_application.date_approved,
            dateExpiry: response.data.credit_application.date_expiry,
            canBeAccepted: response.data.credit_application.can_be_accepted,
            statusReason: response.data.credit_application.status_reason,
            publicRejectedReason: response.data.credit_application.public_rejected_reason,
            originalStatus: response.data.credit_application.original_status,
            teamBankAccountHasChanged: response.data.credit_application.team_bank_account_has_changed,
            numUploadedDocuments: response.data.credit_application.num_uploaded_documents,
            docusignDocuments: response.data.credit_application.docusign_documents
              ? response.data.credit_application.docusign_documents.map(parseClickWrap)
              : undefined,
            plaidBankAccount: response.data.credit_application.plaid_bank_account
              ? parsePlaidAccount(response.data.credit_application.plaid_bank_account)
              : undefined,
          }
        : undefined,
    }
  })
}

export function fetchPlaidReports(teamId: string, plaidAccount: PlaidAccount) {
  const url = `/api/v1/console/teams/${teamId}/plaid-assets-reports/?item_id=${plaidAccount.item.itemId}`
  return axios.get(url).then(parsePlaidReports)
}

export function createPlaidReport(teamId: string, plaidAccount: PlaidAccount) {
  const url = `/api/v1/console/teams/${teamId}/plaid-assets-reports/?item_id=${plaidAccount.item.itemId}`
  return axios.post(url, { account_id: plaidAccount.accountId }).then(parsePlaidReports)
}

export function revealPlaidAccountNumber(teamId: string, accountId: string) {
  const url = `/api/v1/console/teams/${teamId}/plaid-account-number/reveal/?account_id=${accountId}`
  return axios.get(url).then((response) => response.data)
}

export function approveCreditApplication(
  creditApplicationId: number,
  payload: { approved_amount: string; status_reason?: string }
) {
  const url = `/api/v1/console/credit-applications/${creditApplicationId}/approve/`
  return axios.post(url, payload)
}

export function rejectCreditApplication(creditApplicationId: number, payload: { status_reason?: string }) {
  const url = `/api/v1/console/credit-applications/${creditApplicationId}/reject/`
  return axios.post(url, payload)
}

export function resetCreditApplication(creditApplicationId: number) {
  const url = `/api/v1/console/credit-applications/${creditApplicationId}/reset/`
  return axios.post(url)
}

export function resetExpiredCreditApplication(creditApplicationId: number) {
  const url = `/api/v1/console/credit-applications/${creditApplicationId}/reset-expired/`
  return axios.post(url)
}

export function extendCreditApplication(creditApplicationId: number, payload: { approved_amount: string }) {
  const url = `/api/v1/console/credit-applications/${creditApplicationId}/credit-extend/`
  return axios.post(url, payload)
}

export interface EncryptedDocument {
  attachment_file_encrypted: string
  original_filename: string
}

export function uploadCreditApplicationDocuments(
  creditApplicationId: number,
  payload: { attachments: EncryptedDocument[] }
) {
  const url = `${getVGSUrl()}/api/v1/console/credit-applications/${creditApplicationId}/encrypted-documents/`
  const withCredentials = isDevelopmentEnv() ? false : true
  return axios.post(url, payload, { withCredentials })
}

export function revealCreditApplicationDocuments(creditApplicationId: number) {
  const url = `${getVGSUrl()}/api/v1/console/credit-applications/${creditApplicationId}/encrypted-documents/`
  const withCredentials = isDevelopmentEnv() ? false : true
  return axios.get(url, { withCredentials }).then((response) => response.data)
}

export function acceptCreditApplication(creditApplicationId: number) {
  const url = `/api/v1/console/credit-applications/${creditApplicationId}/accept/`
  return axios.post(url)
}

export function fetchAlloyCreditApplications(creditApplicationId: number) {
  const url = `/api/v1/console/credit-applications/${creditApplicationId}/alloy-applications/`
  return axios.get(url).then((response) => response.data)
}

export function createAlloyCreditApplication(creditApplicationId: number) {
  const url = `/api/v1/console/credit-applications/${creditApplicationId}/alloy-applications/`
  return axios.post(url)
}

export function fetchAlloyCreditApplicationProprietaryData(creditApplicationId: number) {
  const url = `/api/v1/console/credit-applications/${creditApplicationId}/alloy-proprietary-data/`
  return axios.get(url).then((response) => response.data)
}

function parsePlaidReports(response: any): PlaidReports {
  return {
    lastReport: response.data.last_report ? parsePlaidReport(response.data.last_report) : undefined,
    lastReadyReport: response.data.last_ready_report
      ? parsePlaidReport(response.data.last_ready_report)
      : undefined,
  }
}

function parsePlaidReport(data: any): PlaidReport {
  return {
    reportId: data.report_id,
    status: data.status,
    dateCreated: data.date_created,
    daysRequested: data.days_requested,
    item: {
      institutionName: data.item?.institution_name || '',
      hasAssetsProduct: data.item?.has_assets_product,
    },
  }
}
